// Phase 32 Plan 32-04 Task 1 — QuillModePicker (RUNE-35).
//
// Bottom-sheet 3-mode picker modal for Scrivener's Quill activation. Per
// UI-SPEC §5 (modal FSM): user activates Quill from the PowerupTray, which
// mounts THIS modal at the viewport bottom; user picks Mark / Claim / Read;
// onSelect fires and the parent (MatchScreen) routes:
//   - Mark / Claim  → mount QuillTargetOverlay (cell-target Pixi overlay)
//   - Read          → fire setPhaseB({mode:'read'}) immediately (self-target)
//
// Composition (UI-SPEC §5):
//   - role="dialog" backdrop scrim, alignItems flex-end → bottom-sheet shape.
//   - Card with title (Fredoka 700 22px) + subtitle (Manrope 600 14px).
//   - 3 mode buttons stacked (gap --space-2); each card icon-row layout with
//     Title (Fredoka 700 16px) + Description (Manrope 600 14px, color
//     --color-text-secondary, line-height 1.5).
//   - Cancel button at the bottom (Btn ghost, size md) — alternative dismiss.
//
// A11y contract (UI-SPEC §9):
//   - role="dialog" + aria-modal=true + aria-labelledby + aria-describedby.
//   - useFocusTrap (Escape + Tab + initial focus + restore).
//   - Tap-outside-to-dismiss via backdrop click (currentTarget === target).
//   - useAnnounce polite on mount + on each mode-select.
//
// Locks:
//   - T-32-04 (UI-SPEC §13 #9): NO browser-storage caching of one-shot state —
//     server's your_powerups_used is the one-shot source of truth.
//   - T-32-05 / Pitfall 8 (UI-SPEC §13 #10): NO opponent private-field reads
//     for affinity letters — this picker is a pure activation gate; the B12
//     firewall is enforced against the QuillTargetOverlay reading
//     playerView.board (public-by-construction).

import { type JSX, useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { Btn } from '../components/Btn'
import { Sheet } from '../components/Sheet'
import { useAnnounce } from '../components/useAnnounce'

/** The 3 modes the Quill can be activated in (Phase 28 server contract). */
export type QuillMode = 'mark' | 'claim' | 'read'

export interface QuillModePickerProps {
  readonly onSelect: (mode: QuillMode) => void
  readonly onDismiss: () => void
}

const MODES: ReadonlyArray<QuillMode> = ['mark', 'claim', 'read']

/** Mode glyph hints — visible icon placeholders until atlas additions ship.
 *  Plan 32-FUTURE-01 backlog replaces these with Pixi-atlas tinted glyphs
 *  (quill_mode_{mark,claim,read}_icon — UI-SPEC §3 atlas additions). */
const MODE_GLYPHS: Record<QuillMode, string> = {
  mark: 'X',
  claim: '+',
  read: '?',
}

export function QuillModePicker({
  onSelect,
  onDismiss,
}: QuillModePickerProps): JSX.Element {
  const intl = useIntl()
  const announce = useAnnounce()

  // Polite SR announcement on mount (UI-SPEC §9 LiveRegion announcements).
  useEffect(() => {
    announce(intl.formatMessage({ id: 'match.quill.picker.title' }), 'polite')
  }, [announce, intl])

  const handleModeSelect = (mode: QuillMode): void => {
    // Polite SR announcement on mode selection (UI-SPEC §9).
    const modeTitle = intl.formatMessage({
      id: `match.quill.mode.${mode}.title`,
    })
    announce(`${modeTitle} selected`, 'polite')
    onSelect(mode)
  }

  return (
    // Bottom-sheet flavor (UI-SPEC §5): scrim + grab handle + focus trap +
    // scrim-tap/Escape dismissal all live in <Sheet>/ModalScrim. Cancel button
    // is the focusable dismiss alternative.
    <Sheet
      onClose={onDismiss}
      labelledBy="quill-picker-title"
      describedBy="quill-picker-subtitle"
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-4)',
        }}
      >
        <header
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-2)',
          }}
        >
          <h2
            id="quill-picker-title"
            style={{
              margin: 0,
              fontFamily: 'var(--font-family-display)',
              fontWeight: 700,
              fontSize: 'var(--font-size-22)',
              color: 'var(--color-text-primary)',
            }}
          >
            <FormattedMessage id="match.quill.picker.title" />
          </h2>
          <p
            id="quill-picker-subtitle"
            style={{
              margin: 0,
              fontFamily: 'var(--font-family-body)',
              fontWeight: 600,
              fontSize: 'var(--font-size-14)',
              lineHeight: 1.5,
              color: 'var(--color-text-secondary)',
            }}
          >
            <FormattedMessage id="match.quill.picker.subtitle" />
          </p>
        </header>

        <div
          className="mode-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: 'var(--space-2)',
          }}
        >
          {MODES.map((mode) => (
            <button
              key={mode}
              type="button"
              data-mode={mode}
              onClick={() => handleModeSelect(mode)}
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                gap: 'var(--space-4)',
                padding: 'var(--space-4)',
                background: 'var(--color-surface-mid)',
                border: '3px solid var(--color-surface-stroke)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--color-text-primary)',
                cursor: 'pointer',
                textAlign: 'left',
                width: '100%',
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  flex: '0 0 auto',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 24,
                  height: 24,
                  fontFamily: 'var(--font-family-display)',
                  fontWeight: 700,
                  fontSize: 'var(--font-size-16)',
                  color: 'var(--color-cta-primary)',
                }}
              >
                {MODE_GLYPHS[mode]}
              </span>
              <span
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--space-1)',
                  flex: '1 1 auto',
                }}
              >
                <span
                  style={{
                    fontFamily: 'var(--font-family-display)',
                    fontWeight: 700,
                    fontSize: 'var(--font-size-16)',
                    color: 'var(--color-text-primary)',
                    lineHeight: 1.2,
                  }}
                >
                  <FormattedMessage id={`match.quill.mode.${mode}.title`} />
                </span>
                <span
                  style={{
                    fontFamily: 'var(--font-family-body)',
                    fontWeight: 600,
                    fontSize: 'var(--font-size-14)',
                    lineHeight: 1.5,
                    color: 'var(--color-text-secondary)',
                  }}
                >
                  <FormattedMessage
                    id={`match.quill.mode.${mode}.description`}
                  />
                </span>
              </span>
            </button>
          ))}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Btn kind="ghost" size="md" onClick={onDismiss}>
            <FormattedMessage id="match.quill.picker.cancel" />
          </Btn>
        </div>
      </div>
    </Sheet>
  )
}
